import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { PageHeader } from "./DashboardLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import {
  Loader2,
  ScanLine,
  FileText,
  Printer,
  ShieldCheck,
  AlertOctagon,
  Sparkles,
  Rocket,
  Download,
  Send,
  CheckCircle2,
  Mail,
  Wrench,
  HardHat,
  Star,
  Trash2,
  Eye,
  EyeOff,
  ExternalLink,
  Zap,
} from "lucide-react";
import { toast } from "sonner";
import { API } from "../lib/api";
import AxeFindingsPanel from "../components/AxeFindingsPanel";
import AuditReportPreview from "../components/AuditReportPreview";

const CATEGORY_META = {
  FOCUS_TRAP: { label: "Focus Trap", color: "bg-red-100 text-red-800 border-red-300" },
  UNLABELED_FORM: { label: "Unlabeled Form", color: "bg-amber-100 text-amber-800 border-amber-300" },
  BROKEN_HEADING: { label: "Heading Hierarchy", color: "bg-blue-100 text-blue-800 border-blue-300" },
  UNREADABLE_MEDIA: { label: "Unreadable Media", color: "bg-purple-100 text-purple-800 border-purple-300" },
};

const MANUAL_CATEGORY_META = {
  MISSING_CAPTIONS: { label: "Missing Video Captions", color: "bg-fuchsia-100 text-fuchsia-900 border-fuchsia-400" },
  UNREMEDIATED_PDF: { label: "Unremediated PDF", color: "bg-orange-100 text-orange-900 border-orange-400" },
  UNTRUSTED_IFRAME: { label: "Untrusted Iframe", color: "bg-rose-100 text-rose-900 border-rose-400" },
};

function renderMarkdown(md) {
  if (!md) return null;
  const lines = md.split("\n");
  const out = [];
  let inList = false;
  let listBuf = [];
  const flushList = () => {
    if (inList) {
      out.push(<ul key={`ul-${out.length}`} className="list-disc pl-6 my-3 space-y-1">{listBuf}</ul>);
      listBuf = [];
      inList = false;
    }
  };
  lines.forEach((raw, idx) => {
    const ln = raw.trimEnd();
    if (ln.startsWith("# ")) { flushList(); out.push(<h1 key={idx} className="font-display text-3xl md:text-4xl font-black tracking-tighter text-black mt-4 mb-2">{ln.slice(2)}</h1>); }
    else if (ln.startsWith("## ")) { flushList(); out.push(<h2 key={idx} className="font-display text-lg md:text-xl font-bold mt-3 mb-1 text-black">{ln.slice(3)}</h2>); }
    else if (ln.startsWith("### ")) { flushList(); out.push(<h3 key={idx} className="font-display text-base font-bold mt-5 mb-1 text-black uppercase tracking-wider border-b border-black pb-1">{ln.slice(4)}</h3>); }
    else if (ln.startsWith("- ") || ln.startsWith("* ")) { inList = true; listBuf.push(<li key={idx} className="text-sm leading-relaxed">{ln.slice(2)}</li>); }
    else if (ln.startsWith("> ")) { flushList(); out.push(<blockquote key={idx} className="border-l-2 border-zinc-400 pl-3 italic text-zinc-600 my-2 text-sm">{ln.slice(2)}</blockquote>); }
    else if (ln.trim() === "") { flushList(); }
    else { flushList(); out.push(<p key={idx} className="text-sm leading-relaxed my-2">{ln}</p>); }
  });
  flushList();
  return out;
}

export default function SiteAuditor() {
  const [url, setUrl] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [auditing, setAuditing] = useState(false);
  const [pitching, setPitching] = useState(false);
  const [audits, setAudits] = useState([]);
  const [selected, setSelected] = useState(null);
  const [converting, setConverting] = useState(false);
  const [prospectEmail, setProspectEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [subjectDefault, setSubjectDefault] = useState("");
  const [subjectVariant, setSubjectVariant] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [sending, setSending] = useState(false);
  const [sentTo, setSentTo] = useState(null);
  const [requestingDev, setRequestingDev] = useState(false);
  const [devRequestedAt, setDevRequestedAt] = useState(null);
  // Three-way list view: "curated" (≥3 issues + saved), "saved" (saved-only),
  // "all" (everything including 0-2 issue sites for archival research).
  const [listMode, setListMode] = useState("curated");
  const [rowBusy, setRowBusy] = useState(null);
  const [bulkPitching, setBulkPitching] = useState(false);

  const loadList = async () => {
    try {
      const r = await api.get("/audits", {
        params: { limit: 100, include_clean: listMode === "all" },
      });
      let data = r.data;
      if (listMode === "saved") data = data.filter((a) => a.is_saved);
      setAudits(data);
      // Initial auto-select only — do NOT snap the current selection back
      // to data[0] just because it's outside the current filter view. The
      // operator may have just audited a low-issue site (filtered out by
      // the default MIN_AUDIT_ISSUES threshold); their selection must
      // survive the subsequent list refresh. If they actually deleted the
      // selected audit, the individual delete handler nulls it out.
      if (!selected && data[0]) setSelected(data[0]);
    } catch {}
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadList(); }, [listMode]);

  // Deep-link: /operator/auditor?audit=<id> jumps straight to a specific
  // audit (used by Geo-Blitz / Competitor Hunt "View findings" links).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const auditId = params.get("audit");
    if (!auditId) return;
    (async () => {
      try {
        const { data } = await api.get(`/audits/${auditId}`);
        setSelected(data);
        setAudits((list) => (list.some((a) => a.id === data.id) ? list : [data, ...list]));
      } catch {}
    })();
  }, []);

  const toggleSave = async (audit) => {
    setRowBusy(audit.id);
    try {
      const endpoint = audit.is_saved ? "unsave" : "save";
      await api.post(`/v1/audits/${audit.id}/${endpoint}`);
      const next = !audit.is_saved;
      setAudits((list) => list.map((a) => a.id === audit.id ? { ...a, is_saved: next } : a));
      if (selected?.id === audit.id) setSelected((s) => s && { ...s, is_saved: next });
      toast.success(next ? "Saved — kept in list permanently" : "Unsaved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update");
    } finally {
      setRowBusy(null);
    }
  };

  const deleteAudit = async (audit) => {
    if (!window.confirm(`Delete audit for ${audit.target_url}? This cannot be undone.`)) return;
    setRowBusy(audit.id);
    try {
      await api.delete(`/audits/${audit.id}`);
      setAudits((list) => list.filter((a) => a.id !== audit.id));
      if (selected?.id === audit.id) setSelected(null);
      toast.success("Audit deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    } finally {
      setRowBusy(null);
    }
  };

  // Bulk send pitch PDFs to every saved audit with a discovered email and an
  // already-generated dossier. Operator confirms a summary breakdown first.
  const bulkPitchSaved = async () => {
    const savedAudits = audits.filter((a) => a.is_saved);
    const eligible = savedAudits.filter(
      (a) => a.discovered_email && a.pitch_dossier && a.pitch_status !== "PITCHED",
    );
    const noEmail = savedAudits.filter((a) => !a.discovered_email).length;
    const noDossier = savedAudits.filter((a) => a.discovered_email && !a.pitch_dossier).length;
    const already = savedAudits.filter(
      (a) => a.discovered_email && a.pitch_dossier && a.pitch_status === "PITCHED",
    ).length;

    if (savedAudits.length === 0) {
      toast.error("No saved audits yet. Star some first.");
      return;
    }
    const lines = [
      `About to pitch ${eligible.length} of ${savedAudits.length} saved audit(s).`,
      "",
      noEmail > 0 ? `${noEmail} skipped — no discovered email` : null,
      noDossier > 0 ? `${noDossier} skipped — no pitch dossier yet` : null,
      already > 0 ? `${already} skipped — already pitched (force-resend available via API)` : null,
      "",
      eligible.length === 0 ? "⚠️ Nothing to pitch right now." : "Continue?",
    ].filter(Boolean).join("\n");
    if (!window.confirm(lines)) return;
    if (eligible.length === 0) return;

    setBulkPitching(true);
    try {
      const r = await api.post("/v1/outreach/bulk-pitch-saved", { force: false });
      toast.success(
        `Queued ${r.data.queued} pitch${r.data.queued === 1 ? "" : "es"} · ${r.data.skipped_no_email} no-email · ${r.data.skipped_no_dossier} no-dossier · ${r.data.skipped_already_pitched} already-sent`,
        { duration: 7000 },
      );
      // Wait briefly for the server to start work then reload so the UI
      // reflects the new pitch_sent_at/pitch_status badges.
      setTimeout(loadList, 3500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk pitch failed");
    } finally {
      setBulkPitching(false);
    }
  };

  // Whenever the selected audit changes, pre-fill the prospect email field
  // with the scraper's best discovered_email and reset the "SENT" badge.
  useEffect(() => {
    if (!selected) {
      setProspectEmail("");
      setSentTo(null);
      setSubject("");
      setSubjectDefault("");
      setSubjectVariant(null);
      return;
    }
    setProspectEmail(selected.discovered_email || "");
    setSentTo(selected.pitch_sent_to || null);
    setDevRequestedAt(selected.developer_assistance_requested_at || null);
    // Fetch the auto-selected subject line for this audit (lawsuit / AccessiBe / default).
    (async () => {
      try {
        const r = await api.get(`/v1/outreach/subject-preview/${selected.id}`);
        setSubjectDefault(r.data?.subject || "");
        setSubject(r.data?.subject || "");
        setSubjectVariant(r.data?.variant || null);
      } catch (_err) {
        setSubjectDefault("");
        setSubject("");
        setSubjectVariant(null);
      }
    })();
  }, [selected?.id]);

  const downloadPdf = async () => {
    if (!selected) return;
    setDownloading(true);
    try {
      const token = localStorage.getItem("ada_token");
      const resp = await fetch(`${API}/v1/audits/download-pdf/${selected.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) {
        const j = await resp.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${resp.status}`);
      }
      const blob = await resp.blob();
      const a = document.createElement("a");
      const objUrl = URL.createObjectURL(blob);
      a.href = objUrl;
      a.download = `ADA-Interceptor-${(selected.business_name || "report").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objUrl);
      toast.success("PDF downloaded");
    } catch (err) {
      toast.error(err.message || "Download failed");
    } finally {
      setDownloading(false);
    }
  };

  const sendPitch = async () => {
    if (!selected) return;
    const email = (prospectEmail || "").trim();
    if (!email || !email.includes("@")) {
      toast.error("Enter a valid prospect email first");
      return;
    }
    setSending(true);
    try {
      const r = await api.post("/v1/outreach/send-pitch", {
        audit_id: selected.id,
        to_email: email,
        subject_override:
          subject.trim() && subject.trim() !== subjectDefault ? subject.trim() : null,
      });
      if (r.data?.sent) {
        setSentTo(email);
        setSelected((s) => s && { ...s, pitch_sent_to: email, pitch_status: "PITCHED" });
        const mode = r.data?.mode === "dry_run" ? " (dry-run · SMTP not configured)" : "";
        toast.success(`Sent to ${email}${mode}`);
        await loadList();
      } else {
        toast.error(r.data?.message || "Send failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const runAudit = async (e) => {
    e?.preventDefault();
    if (!url.trim()) { toast.error("Enter a URL"); return; }
    setAuditing(true);
    try {
      const r = await api.post("/audits", { target_url: url.trim(), business_name: businessName || null });
      toast.success(`Audit complete · ${r.data.total_issues} issue(s)`);
      setSelected(r.data);
      await loadList();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Audit failed");
    } finally { setAuditing(false); }
  };

  const generatePitch = async () => {
    if (!selected) return;
    setPitching(true);
    try {
      const r = await api.post(`/audits/${selected.id}/pitch`);
      setSelected((s) => ({ ...s, pitch_dossier: r.data.dossier, report_status: "REPORT_READY", report_ready_at: new Date().toISOString() }));
      toast.success("Pitch Dossier generated");
      await loadList();
    } catch (err) {
      toast.error("Failed to generate pitch");
    } finally { setPitching(false); }
  };

  const markConverted = async () => {
    if (!selected) return;
    setConverting(true);
    try {
      // 1. update status
      await api.patch(`/audits/${selected.id}/status`, { pitch_status: "CONVERTED" });
      // 2. create deployment
      const host = new URL(selected.target_url.startsWith("http") ? selected.target_url : "https://" + selected.target_url).host;
      const dep = await api.post("/deployments", {
        client_domain: host,
        client_business_name: selected.business_name || host,
        source_audit_id: selected.id,
      });
      toast.success(`Deployment created for ${dep.data.client_domain}`);
      setSelected((s) => ({ ...s, pitch_status: "CONVERTED" }));
      await loadList();
    } catch {
      toast.error("Failed to mark converted");
    } finally { setConverting(false); }
  };

  const grouped = useMemo(() => {
    if (!selected) return {};
    const g = {};
    selected.vulnerabilities?.forEach((v) => {
      g[v.category] = g[v.category] || [];
      g[v.category].push(v);
    });
    return g;
  }, [selected]);

  const groupedManual = useMemo(() => {
    if (!selected) return {};
    const g = {};
    (selected.manual_intervention_required || []).forEach((v) => {
      g[v.category] = g[v.category] || [];
      g[v.category].push(v);
    });
    return g;
  }, [selected]);

  const manualTotal = selected?.manual_intervention_required?.length || 0;

  const requestDevAssistance = async () => {
    if (!selected) return;
    setRequestingDev(true);
    try {
      const r = await api.post(`/v1/audits/${selected.id}/request-developer-assistance`, {});
      if (r.data?.requested) {
        const ts = r.data.requested_at;
        setDevRequestedAt(ts);
        setSelected((s) => s && { ...s, developer_assistance_requested_at: ts });
        toast.success(`Developer assistance requested for ${r.data.manual_finding_count} finding(s)`);
        await loadList();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not log request");
    } finally {
      setRequestingDev(false);
    }
  };

  const printDossier = () => window.print();

  return (
    <div data-testid="site-auditor-page">
      <PageHeader
        eyebrow="Module 03 · Site Auditor & Pitch Lab"
        title="axe-core accessibility audits"
        description="Renders each target in headless Chromium and runs axe-core against WCAG 2 Levels A & AA. Findings are split into Section A (WCAG failures), Section B (best-practice), and Section C (manual review / incomplete). We auto-generate a printable, evidence-based B2B Pitch Dossier from the same structured result."
        testid="auditor-header"
      />

      <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-6 grid grid-cols-1 lg:grid-cols-[420px_minmax(0,1fr)] gap-6">
        {/* Left column — input + audit list */}
        <div>
          <form onSubmit={runAudit} className="border border-zinc-950 p-5 bg-white" data-testid="audit-form">
            <div className="label-xs mb-1">New audit</div>
            <Label className="text-xs font-bold">Target URL</Label>
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com"
              className="rounded-sm h-11 mt-1 font-mono text-sm"
              data-testid="audit-url-input"
              required
            />
            <Label className="text-xs font-bold mt-3 block">Business name (optional)</Label>
            <Input
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              placeholder="Acme Bakery"
              className="rounded-sm h-11 mt-1 text-sm"
              data-testid="audit-business-input"
            />

            <Button
              type="submit"
              disabled={auditing}
              className="w-full mt-4 h-11 rounded-sm bg-blue-700 hover:bg-blue-800 text-white font-bold uppercase tracking-wider"
              data-testid="audit-run-button"
            >
              {auditing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Auditing…</> : <><ScanLine className="w-4 h-4 mr-2" /> Run Audit</>}
            </Button>
          </form>

          <div className="mt-4 border border-zinc-200">
            <div className="bg-zinc-100 px-2 py-1.5 flex items-center gap-1">
              {[
                { id: "curated", label: "Curated", title: "≥3 issues + everything you've saved" },
                { id: "saved", label: "Saved", title: "Only audits you've starred" },
                { id: "all", label: "All", title: "Full archive incl. 0–2 issue sites" },
              ].map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setListMode(t.id)}
                  title={t.title}
                  className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider transition-colors ${
                    listMode === t.id
                      ? "bg-zinc-950 text-white"
                      : "bg-white text-zinc-700 hover:bg-zinc-200 border border-zinc-300"
                  }`}
                  data-testid={`audits-tab-${t.id}`}
                >
                  {t.id === "saved" && <Star className={`w-3 h-3 inline mr-1 ${listMode === t.id ? "fill-amber-400 text-amber-400" : "text-amber-500"}`} />}
                  {t.label}
                </button>
              ))}
              <span className="ml-auto text-[9px] font-mono text-zinc-500">
                {audits.length} {audits.length === 1 ? "audit" : "audits"}
              </span>
            </div>
            {listMode === "saved" && audits.length > 0 && (
              <div className="px-2 py-2 bg-amber-50 border-b border-amber-200 flex items-center justify-between gap-2">
                <span className="text-[10px] font-mono text-amber-900 leading-tight">
                  Send the PDF dossier to every starred audit with a discovered email in one click.
                </span>
                <Button
                  type="button"
                  onClick={bulkPitchSaved}
                  disabled={bulkPitching}
                  className="shrink-0 rounded-sm bg-amber-600 hover:bg-amber-700 text-white font-bold uppercase tracking-wider h-8 text-[10px] px-3"
                  data-testid="bulk-pitch-saved-button"
                  title="Bulk send: PDF + SMTP outreach to every saved audit with a discovered email"
                >
                  {bulkPitching ? (
                    <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Queuing…</>
                  ) : (
                    <><Zap className="w-3 h-3 mr-1" /> Pitch All Saved</>
                  )}
                </Button>
              </div>
            )}
            <ul className="max-h-[420px] overflow-auto">
              {audits.length === 0 && (
                <li className="px-3 py-6 text-center text-xs text-zinc-500">
                  {listMode === "saved"
                    ? "No saved audits. Click the ⭐ on any audit to pin it here."
                    : listMode === "all"
                    ? "No audits yet."
                    : "No audits with 3+ issues yet."}
                </li>
              )}
              {audits.map((a) => (
                <li key={a.id} className={`group border-b border-zinc-100 ${selected?.id === a.id ? "bg-zinc-100 border-l-4 border-l-blue-700" : ""}`}>
                  <div className="flex items-stretch">
                    <button
                      onClick={() => setSelected(a)}
                      className="flex-1 text-left px-3 py-2 hover:bg-zinc-50 transition-colors min-w-0"
                      data-testid={`audit-row-${a.id}`}
                    >
                      <div className="text-xs font-mono truncate flex items-center gap-1.5">
                        {a.is_saved && (
                          <Star
                            className="w-3 h-3 fill-amber-400 text-amber-500 shrink-0"
                            data-testid={`audit-saved-icon-${a.id}`}
                          />
                        )}
                        <span className="truncate">{a.target_url}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500" title="Sum of Section A (WCAG failures) + Section B (best-practice) instances. Section C (manual review) is tracked separately.">
                          {a.total_issues} A+B
                        </span>
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 ${
                          a.pitch_status === "CONVERTED" ? "bg-emerald-600 text-white" :
                          a.pitch_status === "PITCHED" ? "bg-blue-700 text-white" : "bg-zinc-200 text-zinc-700"
                        }`}>{a.pitch_status}</span>
                      </div>
                    </button>
                    <div className="flex flex-col items-center justify-center border-l border-zinc-100 px-1.5 opacity-60 group-hover:opacity-100 transition-opacity">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); toggleSave(a); }}
                        disabled={rowBusy === a.id}
                        title={a.is_saved ? "Unsave" : "Save — keeps this audit in the list even if it has few issues"}
                        className={`p-1.5 hover:bg-zinc-100 ${a.is_saved ? "text-amber-500" : "text-zinc-400 hover:text-amber-500"}`}
                        data-testid={`audit-save-${a.id}`}
                      >
                        <Star className={`w-3.5 h-3.5 ${a.is_saved ? "fill-amber-400" : ""}`} />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); deleteAudit(a); }}
                        disabled={rowBusy === a.id}
                        title="Delete this audit permanently"
                        className="p-1.5 text-zinc-400 hover:text-red-600 hover:bg-red-50"
                        data-testid={`audit-delete-${a.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Right column — vulns + pitch */}
        <div>
          {!selected && (
            <div className="border border-zinc-950 p-12 text-center text-zinc-500">
              <ScanLine className="w-8 h-8 mx-auto mb-3 text-zinc-400" />
              Run your first audit to see findings here.
            </div>
          )}

          {selected && (
            <>
              {/* Vulnerability panel */}
              <div className="border border-zinc-950 p-5 print:hidden" data-testid="vulnerability-panel">
                <div className="flex flex-wrap justify-between items-start gap-3 mb-4">
                  <div>
                    <div className="label-xs">Audit target</div>
                    <a
                      href={selected.target_url.startsWith("http") ? selected.target_url : `https://${selected.target_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-mono text-sm break-all text-blue-700 hover:text-blue-900 hover:underline inline-flex items-center gap-1"
                      data-testid="audit-target-link"
                      title="Open the audited site in a new tab"
                    >
                      {selected.target_url}
                      <ExternalLink className="w-3 h-3 inline shrink-0 opacity-60" />
                    </a>
                    {selected.business_name && <div className="text-xs text-zinc-600 mt-1">{selected.business_name}</div>}
                  </div>
                  <div className="flex flex-wrap gap-2" data-testid="audit-kpi-cards">
                    {(() => {
                      const ax = selected.axe_scan || {};
                      const c = ax.counts || {};
                      const aInst = c.section_a?.instances ?? 0;
                      const bInst = c.section_b?.instances ?? 0;
                      const cInst = c.section_c?.instances ?? 0;
                      const isAxe = selected.scan_engine === "axe-core";
                      if (!isAxe) {
                        return (
                          <>
                            <div className="border border-zinc-950 px-3 py-1.5" title="Legacy heuristic issue count — different engine, not directly comparable to axe.">
                              <div className="label-xs">Legacy findings</div>
                              <div className="font-display text-2xl font-black">{selected.total_issues}</div>
                            </div>
                          </>
                        );
                      }
                      return (
                        <>
                          <div className="border border-zinc-950 px-3 py-1.5 bg-red-50" title="Section A · WCAG 2 A/AA failures (instances)">
                            <div className="label-xs text-red-700">WCAG failures (A)</div>
                            <div className="font-display text-2xl font-black text-red-600" data-testid="kpi-section-a">{aInst}</div>
                          </div>
                          <div className="border border-zinc-950 px-3 py-1.5" title="Section B · Best-practice findings (not a WCAG failure)">
                            <div className="label-xs">Best-practice (B)</div>
                            <div className="font-display text-2xl font-black" data-testid="kpi-section-b">{bInst}</div>
                          </div>
                          <div className="border border-zinc-950 px-3 py-1.5 bg-amber-50" title="Section C · Rules axe could not decide automatically — manual review required">
                            <div className="label-xs text-amber-800">Manual review (C)</div>
                            <div className="font-display text-2xl font-black text-amber-800" data-testid="kpi-section-c">{cInst}</div>
                          </div>
                          <div className="border border-zinc-300 px-3 py-1.5 bg-zinc-50" title="Sum of instances in Sections A + B. Section C is excluded because those items require human judgement.">
                            <div className="label-xs text-zinc-500">Auto-detected total (A+B)</div>
                            <div className="font-display text-2xl font-black">{aInst + bInst}</div>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>

                {/* Scan-outcome banner — makes SUCCESS / PARTIAL / BLOCKED /
                    FAILED explicit. SUCCESS means the scan executed without
                    errors; it does NOT imply accessibility compliance. */}
                {selected.scan_engine === "axe-core" && selected.scan_outcome && (() => {
                  const map = {
                    SUCCESS: { bg: "bg-emerald-50", bd: "border-emerald-300", tx: "text-emerald-900", label: "SCAN EXECUTED" },
                    PARTIAL: { bg: "bg-amber-50",   bd: "border-amber-400",   tx: "text-amber-900",   label: "SCAN PARTIAL" },
                    BLOCKED: { bg: "bg-red-50",     bd: "border-red-400",     tx: "text-red-900",     label: "SCAN BLOCKED" },
                    FAILED:  { bg: "bg-red-50",     bd: "border-red-400",     tx: "text-red-900",     label: "SCAN FAILED" },
                  }[selected.scan_outcome] || null;
                  const ax = selected.axe_scan || {};
                  const counts = ax.counts || {};
                  const cInc = counts.section_c?.rules_incomplete || 0;
                  return map ? (
                    <div data-testid="scan-outcome-banner" className={`border ${map.bd} ${map.bg} p-3 mb-4 text-sm`}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`font-mono text-[10px] font-black uppercase tracking-widest px-2 py-0.5 border ${map.bd} ${map.tx}`}>
                          {map.label}
                        </span>
                        <span className={`text-xs ${map.tx}`}>
                          axe-core {ax.axe_version} · Chromium {ax.browser_version} · {ax.duration_ms}ms
                        </span>
                        <span className="text-[10px] italic text-zinc-600 ml-auto">
                          "Scan executed" reflects tool execution — not accessibility compliance.
                        </span>
                      </div>
                      {selected.scan_outcome === "PARTIAL" && (
                        <div className="mt-1 text-xs text-amber-900" data-testid="partial-coverage-warning">
                          <strong>Partial coverage:</strong> {ax.partial_scan_reason || "some regions not tested"}. Numbers below reflect only what was tested — untested regions are NOT counted as passing.
                        </div>
                      )}
                      {(selected.scan_outcome === "BLOCKED" || selected.scan_outcome === "FAILED") && ax.error && (
                        <div className="mt-1 text-xs">{ax.error}</div>
                      )}
                      {counts.section_a && (
                        <div className="mt-2 flex flex-wrap gap-3 text-xs font-mono">
                          <span>A · WCAG {counts.section_a.rules_failed} rules / {counts.section_a.instances} instances</span>
                          <span>B · Best-practice {counts.section_b?.rules_failed || 0} / {counts.section_b?.instances || 0}</span>
                          <span>C · Manual review {counts.section_c?.rules_incomplete || 0} / {counts.section_c?.instances || 0}</span>
                          {ax.iframe_coverage && !ax.iframe_coverage.all_frames_covered && (
                            <span className="text-amber-900 font-bold">iframes untested: {ax.iframe_coverage.frames_incomplete}</span>
                          )}
                        </div>
                      )}
                      {cInc > 0 && (
                        <div className="mt-1 text-xs text-amber-900" data-testid="manual-review-required">
                          <strong>Manual review required:</strong> {cInc} rule{cInc === 1 ? "" : "s"} axe could not decide automatically — see Section C below.
                        </div>
                      )}
                    </div>
                  ) : null;
                })()}

                {selected.scan_engine === "legacy-heuristic" && (
                  <div data-testid="legacy-scan-banner" className="border border-zinc-400 bg-zinc-50 p-2 mb-4 text-xs text-zinc-700 font-mono">
                    Historical scan (legacy heuristic engine · pre-axe). Re-run to get an axe-core result.
                  </div>
                )}

                {selected.error && (
                  <div className="bg-red-50 border border-red-300 p-3 text-sm text-red-800 mb-4 flex items-start gap-2">
                    <AlertOctagon className="w-4 h-4 mt-0.5 shrink-0" /> Audit error: {selected.error}
                  </div>
                )}

                {selected.scan_engine === "axe-core" && (selected.axe_scan?.counts?.section_a?.instances ?? 0) === 0 && !selected.error && selected.scan_outcome !== "BLOCKED" && selected.scan_outcome !== "FAILED" && (
                  <div className="bg-emerald-50 border border-emerald-300 p-4 text-sm flex items-start gap-2" data-testid="no-wcag-failures-banner">
                    <ShieldCheck className="w-5 h-5 text-emerald-700 shrink-0" />
                    <div>
                      <div className="font-bold text-emerald-900">Section A · No automated WCAG failures detected in the tested scope.</div>
                      <div className="text-emerald-800 text-xs mt-1">
                        This is not a compliance certification. axe automatically checks a subset of WCAG rules; Section B (best-practice) and Section C (manual review / incomplete) findings still require attention — see the breakdown below.
                      </div>
                    </div>
                  </div>
                )}

                {/* Stage-3: shared report preview — reads
                    /api/v1/audits/:id/report and reconciles with the PDF
                    and raw JSON exactly. This replaces both the ad-hoc axe
                    panel and the legacy accordion for anything the shared
                    model can render. */}
                {selected.id && (
                  <AuditReportPreview auditId={selected.id} />
                )}

                {/* Kept for parity: axe findings panel with raw axe evidence
                    when operators need the drill-down that isn't in the
                    normal report. */}
                {selected.scan_engine === "axe-core" && selected.axe_scan?.sections && (
                  <details className="mb-4">
                    <summary className="text-xs uppercase tracking-widest text-zinc-500 cursor-pointer mb-2">
                      Show raw axe drill-down
                    </summary>
                    <AxeFindingsPanel sections={selected.axe_scan.sections} />
                  </details>
                )}

                {selected.vulnerabilities && selected.vulnerabilities.length > 0 && (
                  <>
                    <div className="mt-4 mb-2 flex items-center gap-2" data-testid="section-a-header">
                      <span className="font-display text-xs font-black uppercase tracking-widest bg-zinc-700 text-white px-2 py-1">
                        Section D · Legacy Heuristic
                      </span>
                      <span className="font-mono text-[10px] text-zinc-500 ml-1">
                        {selected.vulnerabilities.length} finding(s) · source: BeautifulSoup detectors (kept for continuity — see Section A above for axe-core WCAG results)
                      </span>
                    </div>
                    <Accordion type="multiple" className="w-full" defaultValue={Object.keys(grouped)}>
                      {Object.entries(grouped).map(([cat, items]) => {
                        const meta = CATEGORY_META[cat] || { label: cat, color: "bg-zinc-100" };
                        return (
                          <AccordionItem key={cat} value={cat} className="border-zinc-200">
                            <AccordionTrigger className="hover:no-underline" data-testid={`vuln-cat-${cat}`}>
                              <div className="flex items-center gap-3">
                                <span className={`px-2 py-0.5 text-xs font-bold uppercase tracking-wider border ${meta.color}`}>{meta.label}</span>
                                <span className="font-mono text-xs text-zinc-500">{items.length}</span>
                              </div>
                            </AccordionTrigger>
                            <AccordionContent>
                              <ul className="space-y-2">
                                {items.map((v, i) => (
                                  <li key={i} className="border-l-2 border-red-600 pl-3 py-1">
                                    <div className="font-mono text-[11px] text-zinc-500">{v.element_selector} <span className="text-zinc-400">@ {v.file_path}</span></div>
                                    <div className="text-sm mt-0.5">{v.error}</div>
                                  </li>
                                ))}
                              </ul>
                            </AccordionContent>
                          </AccordionItem>
                        );
                      })}
                    </Accordion>
                  </>
                )}

                {manualTotal > 0 && (
                  <div className="mt-5 border-2 border-amber-600 bg-amber-50/40" data-testid="section-b-panel">
                    <div className="flex items-center gap-2 px-3 py-2 bg-amber-600 text-white">
                      <HardHat className="w-4 h-4" />
                      <span className="font-display text-xs font-black uppercase tracking-widest">
                        Section B
                      </span>
                      <span className="font-display text-sm font-bold uppercase tracking-wider">
                        Manual Developer Actions Required
                      </span>
                      <span className="ml-auto font-mono text-[11px] bg-amber-900/30 px-1.5 py-0.5">
                        {manualTotal} finding(s)
                      </span>
                    </div>
                    <div className="px-4 py-3 text-[12px] text-amber-900 border-b border-amber-200">
                      The Silent Shield cannot auto-patch these. They require human developer work
                      (caption files, PDF remediation, vendor replacement) and are common ADA-litigation
                      anchors.
                    </div>
                    <div className="px-3 py-2">
                      <Accordion type="multiple" className="w-full" defaultValue={Object.keys(groupedManual)}>
                        {Object.entries(groupedManual).map(([cat, items]) => {
                          const meta = MANUAL_CATEGORY_META[cat] || { label: cat, color: "bg-zinc-100" };
                          return (
                            <AccordionItem key={cat} value={cat} className="border-amber-200">
                              <AccordionTrigger className="hover:no-underline" data-testid={`manual-cat-${cat}`}>
                                <div className="flex items-center gap-3">
                                  <span className={`px-2 py-0.5 text-xs font-bold uppercase tracking-wider border ${meta.color}`}>{meta.label}</span>
                                  <span className="font-mono text-xs text-zinc-500">{items.length}</span>
                                </div>
                              </AccordionTrigger>
                              <AccordionContent>
                                <ul className="space-y-2">
                                  {items.map((v, i) => (
                                    <li key={i} className="border-l-2 border-amber-600 pl-3 py-1">
                                      <div className="font-mono text-[11px] text-zinc-500">{v.element_selector} <span className="text-zinc-400">@ {v.file_path}</span></div>
                                      <div className="text-sm mt-0.5">{v.error}</div>
                                    </li>
                                  ))}
                                </ul>
                              </AccordionContent>
                            </AccordionItem>
                          );
                        })}
                      </Accordion>
                    </div>
                    <div className="px-3 py-3 border-t border-amber-200 flex flex-wrap items-center justify-between gap-3">
                      <div className="text-[11px] font-mono text-amber-900">
                        Upsell opportunity · paid developer engagement
                      </div>
                      {devRequestedAt ? (
                        <span
                          className="inline-flex items-center gap-2 px-3 py-2 bg-emerald-600 text-white font-bold uppercase tracking-wider rounded-sm text-xs"
                          data-testid="dev-assistance-requested-badge"
                          title={`Requested at ${devRequestedAt}`}
                        >
                          <CheckCircle2 className="w-3.5 h-3.5" /> Assistance Requested
                        </span>
                      ) : (
                        <Button
                          type="button"
                          onClick={requestDevAssistance}
                          disabled={requestingDev}
                          className="rounded-sm bg-amber-700 hover:bg-amber-800 text-white font-bold uppercase tracking-wider h-10"
                          data-testid="request-developer-assistance-button"
                        >
                          {requestingDev ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Wrench className="w-4 h-4 mr-2" />
                          )}
                          Request Developer Assistance
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                {selected.pitch_dossier && (
                  <div className="mt-4 pt-4 border-t border-zinc-200" data-testid="outreach-panel">
                    <div className="mb-3">
                      <Label className="text-[10px] font-bold uppercase tracking-wider flex items-center gap-1.5 text-zinc-700">
                        <Mail className="w-3.5 h-3.5" /> Email Subject Line
                        {subjectVariant === "lawsuit_radius" && (
                          <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-rose-100 text-rose-800 border border-rose-300 font-mono" data-testid="subject-variant-badge">
                            lawsuit radius
                          </span>
                        )}
                        {subjectVariant === "accessibe" && (
                          <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-amber-100 text-amber-800 border border-amber-300 font-mono" data-testid="subject-variant-badge">
                            accessibe user
                          </span>
                        )}
                        {subjectVariant === "default" && (
                          <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-zinc-100 text-zinc-700 border border-zinc-300 font-mono" data-testid="subject-variant-badge">
                            default
                          </span>
                        )}
                      </Label>
                      <Input
                        type="text"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        placeholder="Auto-selected based on lead source"
                        className="rounded-sm h-10 mt-1 font-mono text-sm"
                        data-testid="pitch-subject-input"
                      />
                      {subjectDefault && subject.trim() !== subjectDefault && (
                        <div className="mt-1 flex items-center justify-between text-[10px] font-mono text-zinc-500">
                          <span>Overridden — auto default: <span className="text-zinc-800">{subjectDefault}</span></span>
                          <button
                            type="button"
                            onClick={() => setSubject(subjectDefault)}
                            className="uppercase tracking-widest font-bold text-blue-700 hover:underline"
                            data-testid="reset-subject-button"
                          >
                            reset
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-2 items-end">
                      <div>
                        <Label className="text-[10px] font-bold uppercase tracking-wider flex items-center gap-1.5 text-zinc-700">
                          <Mail className="w-3.5 h-3.5" /> Prospect Contact Email
                          {selected.discovered_email && (
                            <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-emerald-100 text-emerald-800 border border-emerald-300 font-mono">
                              scraped
                            </span>
                          )}
                        </Label>
                        <Input
                          type="email"
                          value={prospectEmail}
                          onChange={(e) => setProspectEmail(e.target.value)}
                          placeholder="owner@example.com"
                          className="rounded-sm h-10 mt-1 font-mono text-sm"
                          data-testid="prospect-email-input"
                        />
                      </div>
                      <Button
                        type="button"
                        onClick={downloadPdf}
                        disabled={downloading}
                        className="rounded-sm bg-zinc-950 hover:bg-zinc-800 text-white font-bold uppercase tracking-wider h-10"
                        data-testid="download-pdf-button"
                      >
                        {downloading ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4 mr-2" />
                        )}
                        Download PDF Report
                      </Button>
                      {sentTo ? (
                        <span
                          className="inline-flex items-center gap-2 px-4 py-2 h-10 bg-emerald-600 text-white font-bold uppercase tracking-wider rounded-sm text-sm"
                          data-testid="pitch-sent-confirmation"
                          title={`Sent to ${sentTo}`}
                        >
                          <CheckCircle2 className="w-4 h-4" /> SENT
                        </span>
                      ) : (
                        <Button
                          type="button"
                          onClick={sendPitch}
                          disabled={sending || !prospectEmail.includes("@")}
                          className="rounded-sm bg-blue-700 hover:bg-blue-800 text-white font-bold uppercase tracking-wider h-10 disabled:opacity-40"
                          data-testid="send-pitch-button"
                        >
                          {sending ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Send className="w-4 h-4 mr-2" />
                          )}
                          Send Automated Pitch
                        </Button>
                      )}
                    </div>
                    {selected.discovered_emails?.length > 1 && (
                      <div className="mt-2 text-[10px] text-zinc-500 font-mono">
                        Other candidates discovered: {selected.discovered_emails.slice(1, 5).join(" · ")}
                      </div>
                    )}
                  </div>
                )}

                <div className="mt-5 flex flex-wrap gap-2">
                  <Button
                    onClick={generatePitch}
                    disabled={pitching}
                    className="rounded-sm bg-zinc-950 hover:bg-zinc-800 text-white font-bold uppercase tracking-wide"
                    data-testid="generate-pitch-button"
                  >
                    {pitching ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating…</> : <><Sparkles className="w-4 h-4 mr-2" /> Generate Pitch Dossier</>}
                  </Button>
                  {selected.pitch_dossier && (
                    <>
                      <Button onClick={printDossier} variant="outline" className="rounded-sm border-zinc-950 font-bold uppercase tracking-wide" data-testid="print-dossier-button">
                        <Printer className="w-4 h-4 mr-2" /> Print / PDF
                      </Button>
                      <Button
                        onClick={markConverted}
                        disabled={converting || selected.pitch_status === "CONVERTED"}
                        className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white font-bold uppercase tracking-wide"
                        data-testid="mark-converted-button"
                      >
                        <Rocket className="w-4 h-4 mr-2" /> {selected.pitch_status === "CONVERTED" ? "Converted" : "Mark Converted → Deploy"}
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {/* Pitch dossier preview */}
              {selected.pitch_dossier && (
                <div className="mt-4 border-2 border-zinc-950 bg-white print-dossier" data-testid="pitch-dossier">
                  <div className="bg-zinc-950 text-white px-4 py-2 flex justify-between items-center print:hidden">
                    <div className="label-xs text-zinc-400 flex items-center gap-2"><FileText className="w-3.5 h-3.5" /> Pitch Dossier · printable</div>
                    <div className="font-mono text-[10px] text-zinc-400">B2B / non-technical</div>
                  </div>
                  <div className="p-6 md:p-10 max-w-3xl mx-auto text-black">
                    {renderMarkdown(selected.pitch_dossier)}
                    <div className="mt-8 pt-4 border-t-2 border-black flex justify-between text-[10px] font-bold uppercase tracking-widest text-zinc-600 print:hidden">
                      <span>Prepared by ADA Interceptor</span>
                      <span>Confidential · For prospect use</span>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
